/* eslint-disable react/jsx-boolean-value */
import * as Yup from "yup";
import * as uuid from "uuid";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import AttachAssetsStep from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/AdGroupCreationForm/steps/AttachAssetsStep";
import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import Loader from "../../../../../components/elements/Loader";
import MediaTypeSelector from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/MediaTypeSelector";
import api from "../../../../../api";
import classes from "./UploadCreativesModal.module.scss";
import { AdGroup, AdGroupTemplateCreationBody, AdGroupType, CreativeAsset } from "../../../../../types/adGroupTypes";
import { CreativeAssetFromFile } from "../../../../../types/campaignTypes";
import { CreativeIdsToDelete } from "../../../../../types/creativeTypes";
import { MediaType } from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/types";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createAdGroup,
  createAdGroupTemplate,
  setCampaignBasicDetails,
  uploadCreativeAssetsAndCreateTemplates,
} from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";

type UploadCreativeModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  beeswaxAdvertiserId: number;
  brandgenAdvertiserId: string;
  landingPage: string;
  fetchedAdGroupId: string;
  fetchAndSetAdGroup: () => Promise<void>;
  brandgenCampaignId: string;
  fetchedCreativesAssets: CreativeAsset[];
};

type FormInitialValues = {
  name: string;
  description: string;
  creativeAssets: CreativeAssetFromFile[];
};

const UploadCreativesModal: FC<UploadCreativeModalProps> = ({
  isOpen,
  handleClose,
  beeswaxAdvertiserId,
  brandgenAdvertiserId,
  landingPage,
  fetchedAdGroupId,
  fetchAndSetAdGroup,
  brandgenCampaignId,
  fetchedCreativesAssets,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [mediaType, setMediaType] = useState<MediaType>("display");
  const campaignToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate);
  const { basicDetails } = campaignToCreate!;
  const dispatch = useDispatch();

  const user = useSelector((state: ReduxApplicationState) => state.user);

  const formInitialValues: FormInitialValues = {
    name: "",
    description: "",
    creativeAssets: [],
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Required field."),
  });

  const handleUpload = async (values: FormInitialValues) => {
    const { name, description, creativeAssets } = values;
    try {
      if (creativeAssets.length !== 0 && !isDisabled) {
        setIsLoading(true);
        setIsDisabled(true);
        const adGroupType = creativeAssets[0].fileType as AdGroupType;
        const adGroupToCreateBody = {
          adGroupId: `bg-ad-group-${uuid.v4()}`,
          brandgenAdvertiserId: brandgenAdvertiserId!,
          brandgenCampaignId,
          createdBy: user.currentUser,
          name,
          adGroupType,
          description: description ?? "",
        };
        const createdAdGroup: AdGroup = await createAdGroup(adGroupToCreateBody);

        if (createdAdGroup) {
          const adGroupTemplateToCreateBody: AdGroupTemplateCreationBody = {
            id: `bg-ad-group-template-${uuid.v4()}`,
            brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
            createdBy: createdAdGroup.createdBy,
            name: createdAdGroup.name,
            adGroupType: createdAdGroup.adGroupType,
            description: createdAdGroup.description,
          };
          const createdAdGroupTemplate = await createAdGroupTemplate(adGroupTemplateToCreateBody);
          await uploadCreativeAssetsAndCreateTemplates(
            creativeAssets,
            beeswaxAdvertiserId.toString(),
            brandgenAdvertiserId,
            landingPage,
            createdAdGroup.adGroupId,
            createdAdGroupTemplate.id,
            true,
            api.changeCreative,
            brandgenCampaignId,
          );
        }

        // Deletion
        await api.deleteAdGroup(fetchedAdGroupId);
        const unresolvedPromises = fetchedCreativesAssets.map((fetchedCreativeAsset) => {
          const creativeIdsToDelete: CreativeIdsToDelete = {
            bgCreativeId: fetchedCreativeAsset.bgCreativeId,
            beeswaxCreativeId: fetchedCreativeAsset.beeswaxCreativeId,
          };
          return api.deleteCreative(creativeIdsToDelete);
        });
        await Promise.all(unresolvedPromises);

        await fetchAndSetAdGroup();
        setIsLoading(false);
        setIsDisabled(false);
        handleClose();
        successToaster("Creatives successfully replaced.", 2000);
      } else {
        errorToaster("Please, upload creative assets to continue.", 5000);
      }
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      setIsDisabled(false);
      errorToaster(`There was an error: ${err.message}`);
    }
  };

  const handleMediaTypeChange = (selectedMediaType: MediaType) => {
    setMediaType(selectedMediaType);
    dispatch(setCampaignBasicDetails({ ...basicDetails, isVideoCampaign: selectedMediaType === "video" }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Segment Creation Modal"
      className={classes.container}
      maxWidth="lg"
    >
      <div className={classes.dialogTitle}>
        <Typography variant="h2">Upload new AdGroup</Typography>
      </div>
      {isLoading ? (
        <div className={classes.loader}>
          <Loader isAbsolutePosition={false} size={36} />
        </div>
      ) : (
        <DialogContent className={classes.dialogContent}>
          <Formik
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleUpload}
            validateOnBlur={false}
          >
            {({ values, setFieldValue }) => (
              <Form className={classes.formContainer}>
                <MediaTypeSelector mediaType={mediaType} onChange={handleMediaTypeChange} disableVideo={true} />
                <Typography>Name and Description</Typography>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.name}
                    name="name"
                    variant="outlined"
                    label="Set the name of the ad group *"
                  />
                  <FormFieldError name="name" />
                </div>

                <div className={classes.textArea}>
                  <Field
                    as={BaseInput}
                    value={values.description}
                    name="description"
                    variant="outlined"
                    label="Description"
                    rows={3}
                    multiline
                  />
                  <FormFieldError name="description" />
                </div>
                <AttachAssetsStep creativeAssets={values.creativeAssets} setFieldValue={setFieldValue} limit={100} />

                <Button type="submit" variant="outlined" disabled={isDisabled} className={classes.btnSubmit}>
                  Upload
                </Button>
              </Form>
            )}
          </Formik>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UploadCreativesModal;
